import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Col, Row } from "reactstrap";
import { useAtomValue } from "jotai";
import AppPageHeader from "../components/Headers/AppPageHeader";
import { Module } from "../types/TenantInterfaces";
import PageViewTables from "../components/PageView/PageViewTable/PageViewTables";
import { ScreenSizeAtom } from "../App";
import { Search } from "../components/PageView/PageViewTable/SharedToolbar/SharedToolbar";
import SharedFiltersButtonWithOptions from "../components/PageView/PageViewTable/SharedToolbar/SharedFiltersButtonWithOptions";
import SharedSortBy from "../components/PageView/PageViewTable/SharedToolbar/SharedSortBy";
import useQueriedRecords from "../api/api-hooks/records/useQueriedRecords";
import NewButton from "../elements/NewButton";
import CreateRecordDrawer from "../components/Drawers/Records/CreateRecordDrawer";

interface SharingCenterHeaderI {
  title: string;
  description: string;
  module: Module;
  layout: any;
  initialQuery: {};
  recordNameField: string;
  viewFilter?: any[];
  hiddenFields?: string[];
  SelectRecordsActions?: any;
  stretch?: boolean;
  commentsWidget?: boolean;
  providedCloudID?: string;
  emptyRecordName?: string;
  creatable?: boolean;
  selectedGroup?: any;
  disableAdvancedFilter?: boolean;
  disableSave?: boolean;
  onOpenRecordName?: (data: OpenRecordNameData) => void;
  onSort?: (sortBy: any) => void;
  setQueryCallback?: (query: any) => void;
}

interface OpenRecordNameData {
  moduleAPIName: string;
  recordID: string;
  tab: "Summary";
  record: any;
}

const empty: [] = [];

/**
 * This is a custom table layout similar to SharedToolBar with less functionalities and custom ones.
 * @param param0
 * @returns
 */

const CustomTableLayout = (
  {
    title,
    description,
    module,
    initialQuery,
    layout,
    recordNameField,
    viewFilter = empty,
    hiddenFields = empty,
    SelectRecordsActions,
    stretch,
    commentsWidget,
    providedCloudID,
    emptyRecordName,
    creatable = false,
    selectedGroup,
    disableAdvancedFilter,
    disableSave,
    onSort,
    onOpenRecordName,
    setQueryCallback,
  }: SharingCenterHeaderI,
  ref: Ref<any>
) => {
  const screenSize = useAtomValue(ScreenSizeAtom);
  const mobileScreen = screenSize === "sm" || screenSize === "xs";
  const [query, setQuery] = useState<any>(initialQuery);
  const [simpleSearchValue, setSimpleSearchValue] = useState("");
  const [simpleSearchQuery, setSimpleSearchQuery] = useState<any>(null);
  const [selected, setSelected] = useState({});
  const [createRecordsDrawer, setCreateRecordsDrawer] = useState(false);

  const queryRef = useRef<any>();
  const simpleSearchQueryRef = useRef<any>();

  queryRef.current = query;
  simpleSearchQueryRef.current = simpleSearchQuery;

  useEffect(() => {
    if (selectedGroup) return;
    setQueryCallback && setQueryCallback(query);
  }, [query, selectedGroup]);

  const { records, pagination, recordsLoading, error, errorMsg } =
    useQueriedRecords({
      moduleAPIName: module?.APIName,
      query: simpleSearchQuery || query,
      useDefaultVariables: true,
      disabled: false,
      viewFilters: viewFilter,
      cloudID: providedCloudID,
    });

  /**
   * UseMemo to add RecordName field and also modify the format on some modules (SharedViews).
   */
  const formattedRecords = useMemo(() => {
    if (!records?.length) return [];

    if (!recordNameField) return records;

    const field = module.Fields?.find((f) => f?.APIName === recordNameField);

    if (!field) return records;

    const isReference = ["SingleReference", "MultiReference"].includes(
      field?.FieldType
    );

    return records?.map((r: any) => {
      if (module?.APIName === "SharedViews") {
        if (!!r?.IsSubView) {
          r.Authentication = r?.RecordIds?.[0]?.Authentication;
        }
      }

      r[recordNameField] = r[recordNameField] || emptyRecordName;

      r["_RecordName"] =
        (isReference
          ? r[recordNameField]?._RecordName ?? ""
          : r[recordNameField]) ??
        emptyRecordName ??
        "(No Name)";

      return r;
    });
  }, [recordsLoading, records, query, emptyRecordName]);

  const sortedByRecordName = useMemo(() => {
    if (query?.OrderBy?.Field !== "_RecordName" || !recordNameField)
      return null;
    const sortQuery = query?.OrderBy;

    if (sortQuery.Direction === "ASC") {
      return formattedRecords.sort((a: any, b: any) => {
        const aField = a[recordNameField || "_RecordName"];
        const bField = b[recordNameField || "_RecordName"];

        return aField?.localeCompare(bField);
      });
    }
    if (sortQuery.Direction === "DESC") {
      return formattedRecords.sort((a: any, b: any) => {
        const aField = a[recordNameField || "_RecordName"];
        const bField = b[recordNameField || "_RecordName"];

        return bField?.localeCompare(aField);
      });
    }
  }, [formattedRecords, query]);

  useImperativeHandle(ref, () => ({
    setQuery,
  }));

  const clearSimpleSearch = useCallback(() => setSimpleSearchValue(""), []);

  const stableOpenRecordName = useCallback((data: OpenRecordNameData) => {
    onOpenRecordName && onOpenRecordName(data);
  }, []);

  /**
   * Change the query's sort by parameters
   */
  const updateSortBy = useCallback((sortBy: any) => {
    if (onSort) return onSort(sortBy);

    let q = queryRef.current;
    let otherQ = simpleSearchQueryRef.current;

    q.OrderBy = sortBy ? sortBy : null;
    otherQ && (otherQ.OrderBy = q.OrderBy);

    if (q.OrderBy === null) {
      delete q.OrderBy;
    }

    if (otherQ && otherQ.OrderBy === null) {
      delete otherQ.OrderBy;
    }

    q = { ...q };
    otherQ && (otherQ = { ...otherQ });

    otherQ && setSimpleSearchQuery(otherQ);
    setQuery(q);
  }, []);

  const FiltersButton = (
    //@ts-ignore
    <SharedFiltersButtonWithOptions
      currentModule={module!}
      query={query}
      setQuery={setQuery}
      simpleSearchValue={simpleSearchValue}
      setSimpleSearchValue={setSimpleSearchValue}
      clearSimpleSearch={clearSimpleSearch}
      disableAdvancedFilter={disableAdvancedFilter}
      disableSave={disableSave}
    />
  );

  const SortBy = (
    <SharedSortBy
      query={query}
      updateSortBy={updateSortBy}
      // @ts-ignore
      view={{ ModuleAPIName: module?.APIName }}
    />
  );

  const selectedRecordKeys = Object.keys(selected);
  const selectedRecordsLength = selectedRecordKeys.length;
  const isSelection = selectedRecordsLength > 0;
  const selectedRecords: any[] = [];
  let selectedRecordsIds: string[] = [];

  if (isSelection) {
    for (const key of selectedRecordKeys) {
      if (records?.length && records[key]) {
        selectedRecords.push(records[key]);
        selectedRecordsIds.push(records[key].id);
      }
    }
  }

  return (
    <>
      <AppPageHeader
        borderless={true}
        title={title}
        description={description}
        size="sm"
        className="tw:my-2 tw:px-2 tw:py-3"
      />
      <div>
        {mobileScreen ? (
          <>
            <Col className="p-0">
              <Row
                className="d-flex justify-content-between align-items-center m-2"
                style={{ gap: "10px" }}
              >
                <div style={{ width: "70%" }}>
                  <Search
                    isMobile={true}
                    clearSimpleSearch={clearSimpleSearch}
                    query={query}
                    currentModule={module!}
                    setSimpleSearchQuery={setSimpleSearchQuery}
                    setSimpleSearchValue={setSimpleSearchQuery}
                  />
                </div>
                <div>{FiltersButton}</div>
                <div>{SortBy}</div>
              </Row>
              <Row className="d-flex justify-content-between mt-4 ml-2 mr-2">
                <PageViewTables
                  currentModule={module}
                  simpleSearchQuery={simpleSearchQuery}
                  query={query}
                  setQuery={(query: any) => {
                    if (simpleSearchQuery) setSimpleSearchQuery(query);
                    else setQuery(query);
                  }}
                  foundLayout={layout}
                  updateSortBy={updateSortBy}
                  selected={selected}
                  setSelected={setSelected}
                  records={sortedByRecordName ?? formattedRecords}
                  pagination={pagination}
                  recordsLoading={recordsLoading}
                  error={error}
                  errorMsg={errorMsg}
                  moduleAPIName={module?.APIName}
                  selectableRows={!!SelectRecordsActions}
                  onSort={updateSortBy}
                  selectedRecords={selected}
                  setSelectedRecords={setSelected}
                  data={formattedRecords}
                  loading={recordsLoading}
                  isSelection={Object.keys(selected)?.length > 0}
                  onOpenRecordName={stableOpenRecordName}
                  selectedRecordsLength={selectedRecordsLength}
                  defaultRecordLayout={layout}
                  view={{
                    ModuleAPIName: module?.APIName,
                    TableLayouts: [layout],
                  }}
                  columnAdd={false}
                  defaultHiddenHeaders={hiddenFields}
                  nameField={"Name"}
                  stretch={stretch}
                  commentsWidget={commentsWidget}
                  isCustomTable
                  disableComments
                  dragDisabled
                />
              </Row>
            </Col>
          </>
        ) : (
          <>
            <div className="tw:flex tw:mb-3 tw:justify-between">
              <div className="tw:flex tw:wrap tw:pl-2" style={{ gap: "10px" }}>
                {creatable && (
                  <NewButton
                    onlyNew
                    onClick={() => setCreateRecordsDrawer(true)}
                    options={undefined}
                    onlyOptions={false}
                  />
                )}
                <Search
                  isMobile={false}
                  clearSimpleSearch={clearSimpleSearch}
                  query={query}
                  currentModule={module!}
                  setSimpleSearchQuery={setSimpleSearchQuery}
                  setSimpleSearchValue={setSimpleSearchQuery}
                />
                {FiltersButton}
                {SortBy}
              </div>
            </div>
            <div className="tw:px-2">
              <PageViewTables
                currentModule={module}
                simpleSearchQuery={simpleSearchQuery}
                query={query}
                setQuery={(query: any) => {
                  if (simpleSearchQuery) setSimpleSearchQuery(query);
                  else setQuery(query);
                }}
                foundLayout={layout}
                updateSortBy={updateSortBy}
                selected={selected}
                setSelected={setSelected}
                records={formattedRecords}
                pagination={pagination}
                recordsLoading={recordsLoading}
                error={error}
                errorMsg={errorMsg}
                moduleAPIName={module?.APIName}
                selectableRows={!!SelectRecordsActions}
                onSort={updateSortBy}
                selectedRecords={selected}
                setSelectedRecords={setSelected}
                data={formattedRecords}
                loading={recordsLoading}
                isSelection={Object.keys(selected)?.length > 0}
                onOpenRecordName={stableOpenRecordName}
                selectedRecordsLength={selectedRecordsLength}
                defaultRecordLayout={layout}
                view={{
                  ModuleAPIName: module?.APIName,
                  TableLayouts: [layout],
                }}
                columnAdd={false}
                defaultHiddenHeaders={hiddenFields}
                nameField={"Name"}
                stretch={stretch}
                commentsWidget={commentsWidget}
                isCustomTable
                disableComments
                dragDisabled
              />
            </div>
          </>
        )}
      </div>
      {isSelection && SelectRecordsActions && (
        <SelectRecordsActions
          selectedRecordIds={selectedRecordsIds}
          moduleAPIName={module?.APIName}
          onClear={() => setSelected({})}
        />
      )}
      <CreateRecordDrawer
        open={createRecordsDrawer}
        onClose={() => setCreateRecordsDrawer(false)}
        moduleAPIName={module?.APIName}
        onCreate={() => {
          setCreateRecordsDrawer(false);
        }}
        onCreateForm={undefined}
        prefill={undefined}
      />
    </>
  );
};

export default forwardRef(CustomTableLayout);
